import {
  getDashboardZoom,
  getVisualViewport,
  layoutToVisual,
  layoutPx,
} from 'dashboard/helper/dashboardZoom';

// Aceleres: cálculo de posição do popup de menu de contexto (chevron), sem
// depender de nenhum estado de componente — usado por Message.vue (menu da
// bolha) e StickerAlbum.vue (menu de cada sticker do par). Extraído daqui
// pra não duplicar entre os dois.

export function findBubble(e) {
  const container =
    e.target?.closest?.('.message-bubble-container') ||
    e.currentTarget?.closest?.('.message-bubble-container');
  if (!container) return null;
  return container.querySelector('.right-bubble, .left-bubble');
}

export function findContextMenuTrigger(e) {
  const currentTarget = e?.currentTarget;
  if (currentTarget?.classList?.contains('aceleres-bubble-chevron')) {
    return currentTarget;
  }

  return e?.target?.closest?.('.aceleres-bubble-chevron') || null;
}

export function isMobileMessageMenu() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(max-width: 767px) and (pointer: coarse)').matches;
}

// Aceleres 2026-08-25: as tres copias desta aritmetica (aqui, em
// MessageContextMenu.vue e no ui/ContextMenu.vue) foram unificadas em
// helper/dashboardZoom.js, que documenta os dois espacos de coordenadas.
// Reexportamos os nomes antigos para nao mexer em quem ja os importa.
export { getDashboardZoom };

export function getVisualViewportSize() {
  // Aceleres 2026-08-25 -- CORRIGIDO. Aqui multiplicava-se a viewport pelo zoom,
  // com o comentario de que `innerWidth` viria "antes do zoom". Medido no Chrome
  // 151 (Input.dispatchMouseEvent + getBoundingClientRect, com `html{zoom:.8}`):
  // `innerWidth`, `visualViewport.width` e os DOMRects vem TODOS em espaco
  // visual. Multiplicar encolhia a viewport para 80% e o clamp puxava o popup
  // para dentro cerca de 300px numa tela de 1600 — desviando-o do cursor.
  return getVisualViewport();
}

export function fixedPosition(value, zoom = getDashboardZoom()) {
  return layoutPx(value, zoom);
}

export function getMobileFocusedMessageCenter({
  bubbleTop,
  bubbleBottom,
  bubbleHeight,
  viewportHeight,
  viewportWidth,
  zoom = getDashboardZoom(),
}) {
  const resolvedHeight = Number(bubbleHeight) || 72;
  const originalCenter =
    Number.isFinite(Number(bubbleTop)) && Number.isFinite(Number(bubbleBottom))
      ? (Number(bubbleTop) + Number(bubbleBottom)) / 2
      : viewportHeight * 0.36;
  const usesCompactMenu = viewportWidth <= 640 && viewportHeight <= 650;
  const reactionSpace = layoutToVisual(62, zoom);
  const menuSpace = layoutToVisual(usesCompactMenu ? 144 : 180, zoom);
  const safeGap = layoutToVisual(12, zoom);
  const minimumCenter = resolvedHeight / 2 + reactionSpace + safeGap;
  const preferredMaximum = viewportHeight * (usesCompactMenu ? 0.38 : 0.43);
  const maximumCenter = Math.max(
    minimumCenter,
    Math.min(
      preferredMaximum,
      viewportHeight - menuSpace - resolvedHeight / 2 - safeGap
    )
  );

  return Math.min(Math.max(originalCenter, minimumCenter), maximumCenter);
}

export function getMobileFocusedMessageHorizontalOffset({
  orientation,
  bubbleLeft,
  bubbleRight,
  targetLeft,
  targetRight,
}) {
  return orientation === 'right'
    ? targetRight - bubbleRight
    : targetLeft - bubbleLeft;
}

export function getMobileActionsTop({
  focusedCenter,
  bubbleHeight,
  viewportHeight,
  zoom = getDashboardZoom(),
}) {
  const resolvedHeight = Number(bubbleHeight) || 72;
  const resolvedCenter = Number(focusedCenter) || viewportHeight * 0.36;
  const gap = layoutToVisual(10, zoom);
  const bubbleBottom = resolvedCenter + resolvedHeight / 2;
  return bubbleBottom + gap;
}

export function computeMenuAnchor(e, opts = {}) {
  const cx = typeof e.clientX === 'number' ? e.clientX : 0;
  const cy = typeof e.clientY === 'number' ? e.clientY : 0;
  const pointerAnchor = opts.pointerAnchor === true;
  // Medidas DECLARADAS em CSS. Com `html { zoom: .8 }` elas ocupam 248x272 no
  // ecra, que e a escala de `clientX` e dos DOMRects usados logo abaixo; sem
  // `layoutToVisual` a conta reservava espaco a mais e empurrava o popup.
  const POPUP_W = layoutToVisual(310);
  const POPUP_H = layoutToVisual(340);
  const configuredOffset = typeof opts.offset === 'number' ? opts.offset : 4;
  const OFFSET = pointerAnchor ? 0 : configuredOffset;
  const PAD = 8;
  const {
    zoom,
    width: viewportWidth,
    height: viewportHeight,
  } = getVisualViewportSize();

  // Aceleres 2026-07-01: usa o container scrollável da MessagesView
  // (`.scrollbar-thin.flex-grow.h-full` — o wrapper flex-col do
  // MessagesView.vue) como boundary pra decidir up/down do popup.
  // Assim garantimos que o emoji picker / menu SEMPRE nasce dentro
  // dessa área — nunca embaixo do header nem embaixo da reply-box.
  const messagesArea = document.querySelector(
    '.flex.flex-col.justify-between.flex-grow.h-full.min-w-0.m-0.scrollbar-thin'
  );
  const areaRect = messagesArea ? messagesArea.getBoundingClientRect() : null;
  const areaTop = areaRect ? areaRect.top : 0;
  const areaBottom = areaRect ? areaRect.bottom : viewportHeight;

  const bubble = findBubble(e);
  const bubbleRect = bubble?.getBoundingClientRect();
  const actionsAbove =
    Boolean(opts.actionsAbove) ||
    (bubbleRect?.height ?? 0) >= layoutToVisual(240, zoom);
  const focusedMessagePosition = {
    bubbleTop: bubbleRect?.top,
    bubbleBottom: bubbleRect?.bottom,
    bubbleHeight: bubbleRect?.height,
    viewportHeight,
    viewportWidth,
    zoom,
  };
  const focusedCenter = getMobileFocusedMessageCenter(focusedMessagePosition);
  const trigger = findContextMenuTrigger(e);
  const anchor = pointerAnchor
    ? null
    : opts.anchor || trigger || opts.verticalAnchor || bubble;
  const anchorRect = anchor?.getBoundingClientRect();
  const opensRight =
    pointerAnchor ||
    opts.orientation === 'left' ||
    (!opts.orientation && bubble?.classList.contains('left-bubble'));
  const anchorCenterX = anchorRect
    ? anchorRect.left + anchorRect.width / 2
    : cx;
  let desiredLeft = anchorCenterX - POPUP_W / 2;
  if (pointerAnchor) {
    desiredLeft = cx + OFFSET;
  } else if (anchorRect) {
    desiredLeft = opensRight
      ? anchorRect.right + OFFSET
      : anchorRect.right - POPUP_W;
  }
  const leftPx = Math.min(
    Math.max(desiredLeft, PAD),
    Math.max(PAD, viewportWidth - POPUP_W - PAD)
  );

  // Vertical: usa bounds do container em vez de window.innerHeight.
  const verticalAnchor = anchor;
  const verticalAnchorRect = pointerAnchor
    ? null
    : verticalAnchor?.getBoundingClientRect();
  const anchorTop = verticalAnchorRect?.top ?? cy;
  const anchorBottom = verticalAnchorRect?.bottom ?? cy;

  let topPx = null;
  let bottomPx = null;
  let direction;
  const spaceBelow = areaBottom - anchorBottom;
  const spaceAbove = anchorTop - areaTop;
  const needed = POPUP_H + OFFSET + PAD;
  const fitsAbove = spaceAbove >= needed;
  const fitsBelow = spaceBelow >= needed;
  if (fitsBelow && !fitsAbove) {
    direction = 'down';
    topPx = anchorBottom + OFFSET;
  } else if (fitsAbove && !fitsBelow) {
    direction = 'up';
    bottomPx = viewportHeight - anchorTop + OFFSET;
  } else if (spaceBelow >= spaceAbove) {
    direction = 'down';
    topPx = anchorBottom + OFFSET;
  } else {
    direction = 'up';
    bottomPx = viewportHeight - anchorTop + OFFSET;
  }

  const originY = direction === 'down' ? 'top' : 'bottom';

  return {
    left: fixedPosition(leftPx, zoom),
    right: 'auto',
    top: topPx === null ? '' : fixedPosition(topPx, zoom),
    bottom: bottomPx === null ? '' : fixedPosition(bottomPx, zoom),
    direction,
    horizontalDirection: opensRight ? 'right' : 'left',
    transformOrigin: `${originY} center`,
    pointerAnchor,
    anchorLeft: anchorRect?.left ?? cx,
    anchorTop,
    anchorRight: anchorRect?.right ?? cx,
    anchorBottom: anchorRect?.bottom ?? cy,
    anchorWidth: anchorRect?.width ?? 0,
    anchorHeight: anchorRect?.height ?? 0,
    // Aceleres 2026-08-18 -- a BOLHA, alem da ancora.
    //
    // A ancora do menu e o chevron: um botao pequeno colado no canto de cima
    // da bolha. Serve bem para o menu, que e estreito, mas quando o "+" troca
    // o menu pelo seletor de emoji inteiro (~420px de altura) o popup passa a
    // ser medido a partir desse cantinho e sobe muito acima da mensagem — foi
    // o afastamento relatado.
    //
    // Por isso o retangulo da bolha viaja junto: quem posiciona escolhe a
    // ancora conforme o que esta a mostrar (menu -> chevron, seletor -> bolha)
    // sem ter de reencontrar o elemento noutro componente, do outro lado de um
    // Teleport. Vai como numeros, e nao como o elemento, para acompanhar o que
    // as chaves `anchor*` acima ja faziam — sao lidas do mesmo objeto.
    bubbleLeft: bubbleRect?.left ?? null,
    bubbleTop: bubbleRect?.top ?? null,
    bubbleRight: bubbleRect?.right ?? null,
    bubbleBottom: bubbleRect?.bottom ?? null,
    bubbleWidth: bubbleRect?.width ?? null,
    bubbleHeight: bubbleRect?.height ?? null,
    bubbleCenter: bubbleRect ? bubbleRect.left + bubbleRect.width / 2 : null,
    focusedCenter,
    actionsAbove,
  };
}
